import { Observable, of as observableOf, Subject } from 'rxjs';

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  output,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatOption, MatOptionSelectionChange } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  debounceTime,
  filter,
  map,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { HIRE_ITEM_SEARCH } from 'src/testIds';
import { Product } from '../../models';
import { BookingItem } from '../../models/booking-item';
import { DocketService, ProductService } from '../../shared';
import { ProductCategoryService } from '../../shared/services/product-category.service';
import { TestIdDirective } from '../directives/test-id.directive';
import { extractQuantity } from '../helpers/extractQuantity';
import { normaliseItemCode } from '../helpers/normaliseItemCode';
import { ItemService } from '../index';
import { BookingItemNamePipe } from '../pipes/booking-item-name.pipe';
import {
  BookingItemService,
  mapItemToBookingItem
} from '../services/booking-item.service';
import { BookingService } from '../services/booking.service';
import { CategoryGridDialogComponent } from './category-grid-item-selection/category-grid-dialog/category-grid-dialog.component';
import * as GRID_TEST_IDS from './testIds';
import { SearchResultOptionComponent } from './search-result-option.component';
import { VirtualProductLabelComponent } from '../virtual-product-label/virtual-product-label.component';

@Component({
  selector: 'item-search',
  templateUrl: './item-search.component.html',
  providers: [
    DocketService,
    ProductService,
    ProductCategoryService,
    BookingService,
    BookingItemService,
    ItemService,
    BookingItemNamePipe
  ],
  standalone: true,
  imports: [
    MatFormField,
    TestIdDirective,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatHint,
    MatAutocomplete,
    NgIf,
    NgFor,
    MatOption,
    MatIconButton,
    MatIcon,
    AsyncPipe,
    BookingItemNamePipe,
    SearchResultOptionComponent,
    VirtualProductLabelComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemSearchComponent implements OnDestroy, OnInit {
  private ngUnsubscribe = new Subject<void>();
  filterControl = new UntypedFormControl();
  filteredProducts: Observable<Product[]>;
  filteredBookingItems: Observable<BookingItem[]>;
  quantity = 1;
  @Input() searchType;
  optionSelected = output<any>();
  @ViewChild(MatAutocompleteTrigger, { static: true })
  autoCompleteTrigger: MatAutocompleteTrigger;
  testIds = HIRE_ITEM_SEARCH;
  gridTestIds = GRID_TEST_IDS;

  constructor(
    private dialog: MatDialog,
    private productService: ProductService,
    private itemService: ItemService,
    private bookingService: BookingService,
    private bookingItemService: BookingItemService,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit() {
    if (this.searchType === 'docket') {
      this.filteredProducts = this.filterControl.valueChanges.pipe(
        debounceTime(200),
        takeUntil(this.ngUnsubscribe),
        switchMap((value) => this.searchForDocketProductsAndItems(value))
      );
    } else if (this.searchType === 'booking') {
      this.filteredBookingItems = this.filterControl.valueChanges.pipe(
        debounceTime(200),
        takeUntil(this.ngUnsubscribe),
        switchMap((value) =>
          this.searchForBookingProductsCategoriesAndItems(value)
        )
      );
    }
  }

  private searchForBookingProductsCategoriesAndItems(searchTerm) {
    if (searchTerm) {
      const qty = extractQuantity(searchTerm);
      if (qty) {
        searchTerm = searchTerm.substr(qty.length + 1);
        this.quantity = +qty;
      } else {
        this.quantity = 1;
      }
      return this.bookingService.search(searchTerm).pipe(
        map(this.bookingItemService.productsToPossibleBookingItems),
        map((results) => {
          return results;
        })
      );
    } else {
      return observableOf([]);
    }
  }

  searchForDocketProductsAndItems(text: string): Observable<Product[]> {
    if (!text || typeof text !== 'string') {
      return observableOf([]);
    }
    let normalisedText = normaliseItemCode(text);
    const quantity = extractQuantity(normalisedText);
    if (quantity) {
      normalisedText = normalisedText.substr(quantity.length + 1);
    }
    if (normalisedText) {
      return this.productService.searchProductsAndItems(normalisedText);
    }
    return observableOf([]);
  }

  quickAddItem() {
    if (this.autoCompleteTrigger.activeOption) {
      return;
    }
    let search = this.filterControl.value;
    const qtyString = extractQuantity(search);
    if (qtyString) {
      search = search.substr(qtyString.length + 1);
    }
    this.addItemByCode(search);
  }

  private addItemByCode(search) {
    this.itemService
      .findItemByCode(search)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((items) => items.length > 0),
        map((items) => {
          if (this.searchType === 'docket') {
            let product = items[0].product;
            delete items[0].product;
            product.items = [items[0]];
            return product;
          }
          return mapItemToBookingItem(items[0]);
        })
      )
      .subscribe((product) => {
        this.optionSelected.emit(product);
      });
  }

  listOptionSelected(option: MatOptionSelectionChange<Product | BookingItem>) {
    this.optionSelected.emit(option.source.value);
  }

  autoCompleteMap(value) {
    return '';
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  openCategoryGrid() {
    this.dialog
      .open(CategoryGridDialogComponent, {
        minWidth: '80vw',
        height: '80vh',
        viewContainerRef: this.viewContainerRef,
        data: {
          disableCurrentlyUnavailable: this.searchType === 'docket'
        }
      })
      .afterClosed()
      .pipe(filter((result) => Boolean(result)))
      .subscribe((result) => {
        this.addItemByCode(result);
      });
  }
}
